import StdForm from '@shared/components/forms/auto-form/std-form/std-form.vue';
import { Component, Prop } from 'vue-property-decorator';
import EntityTableProvider from '@shared/components/tables/entity-table-provider/entity-table-provider.vue';
import EntityTable from '@shared/components/tables/entity-table/entity-table.vue';
import BpcPageTitle from '@shared/layouts/components/page-title/page-title.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import ConfirmationModal from '@shared/components/modals/confirmation-modal/confirmation-modal.vue';
import { ValidationObserver } from 'vee-validate';
import EntityDeleteModalProvider from '@shared/components/modals/entity-delete-modal-provider/entity-delete-modal-provider.vue';
import { mixins } from 'vue-class-component';
import AbstractTable from '@shared/components/tables/abstract-table/abstract-table';
import StdFilter from '@shared/components/filters/std-filter/std-filter.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSync } from '@fortawesome/free-solid-svg-icons';
import { Enrollment } from '@modules/activities/interfaces/enrollment';
import BpcDrawerModal from '@shared/components/modals/drawer-modal/drawer-modal.vue';
import AuthorizationHelper from '@auth/services/authorization.helper';
import { ActivityGroup } from '@activities/interfaces/activity-group';
import EntityFormProvider from '@shared/components/forms/auto-form/entity-form-provider/entity-form-provider';
import SendMessageUsingTemplateMixin from '@shared/components/tables/abstract-table/send-message-using-template-mixing';
import DownloadPdfUsingTemplateMixin from '@shared/components/tables/abstract-table/download-pdf-using-template-mixing';
import ModulesHelper from '@core/services/modules.helper';
import CustomFilterSelector from '@shared/components/filters/custom-filter-selector/custom-filter-selector.vue';
import ExtendedFilter from '@shared/components/filters/extended-filter/extended-filter.vue';

library.add(faSync);

@Component({
  name: 'bpc-waiting-list-table',
  components: {
    'bpc-entity-table-provider': EntityTableProvider,
    'bpc-custom-filter-selector': CustomFilterSelector,
    'bpc-entity-table': EntityTable,
    'bpc-page-title': BpcPageTitle,
    'font-awesome-icon': FontAwesomeIcon,
    'bpc-std-form': StdForm,
    'bpc-std-filter': StdFilter,
    ValidationObserver,
    'bpc-drawer-modal': BpcDrawerModal,
    'bpc-extended-filter': ExtendedFilter,
    'bpc-entity-delete-modal-provider': EntityDeleteModalProvider,
    'bpc-confirmation-modal': ConfirmationModal,
  },
})
export default class WaitingListTable extends mixins(
  AbstractTable,
  SendMessageUsingTemplateMixin,
  DownloadPdfUsingTemplateMixin,
) {
  declare $refs: AbstractTable['$refs'] & {
    confirmEnrollmentConfirmationModal: InstanceType<typeof ConfirmationModal> & {
      onOpenModal(id): void;
      onCloseModal(): void;
    };
    stdCancelForm: InstanceType<typeof StdForm> & {
      formModel: any;
      formProvider: EntityFormProvider;
    };
  };

  @Prop({ type: Boolean, default: false }) readonly showActionButtonText: boolean;
  @Prop({ type: Boolean, default: false }) declare readonly isInput: boolean;
  @Prop({ type: Boolean, default: true }) readonly showTableHeader: boolean;
  @Prop(String) readonly fieldForStorage: string;
  @Prop({ type: Array, default: () => ['part1', 'part2'] })
  declare readonly selectConfigName: string[];
  @Prop({ type: Array, default: () => ['type'] })
  declare readonly filtersExcluded: string[];
  @Prop({ type: String, default: 'bulk-edit' }) readonly bulkEditFormConfigName: string;
  @Prop(Object) readonly externalFormModel: Record<string, any>;

  public entity: Enrollment = null;
  public entityName = 'activity-group-waiting-contact';
  public pluralEntityName = 'activity-group-waiting-contacts';
  public showDrawerSettingSection: boolean = false;
  public showSettingForm: boolean = false;
  public permissionEntityNameToUse = 'activity_group_waiting_contacts';
  public selectedRowEntity = null;
  public warningMessagesBeforeConfirmEnrollment: [] = [];
  public selectedActivityGroupToEnroll: ActivityGroup = null;
  public isCancelingEntity: boolean = false;

  public flagsToCheckBeforeLoadEntities: string[] = [
    'configLoaded',
    'filtersLoaded',
    'paginationLoaded',
    'orderLoaded',
    'urlParametersLoaded',
  ];

  public customPageActions = {
    bulkSendMessageUsingTemplate: {
      activated: false,
      type: 'action',
      icon: 'envelope', //'times',
      variant: 'info',
      text: 'send_message_using_template',
      show: false,
      event: 'bulk-send-message-using-template',
      selectElements: true,
      tag: 'left',
      permissions: ['show'],
    },
  };

  customOnPageAction($event: string) {
    switch ($event) {
      case 'bulk-send-message-using-template':
        this.onBulkSendMessageUsingTemplate(this.selection);
        break;
    }
  }

  extendUpdatePageActions() {
    if (this.config) {
      for (const activatedPageAction of this.config.data.activatedPageActions) {
        if (this.customPageActions[activatedPageAction]) {
          this.customPageActions[activatedPageAction].activated = true;
        }
      }
    }

    this.customPageActions.bulkSendMessageUsingTemplate.show =
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed &&
      this.$modulesService.hasModuleActivated(ModulesHelper.COMMUNICATIONS_MODULE_NAME);

    // Check Permissions
    for (const activatedPageAction of this.config.data.activatedPageActions) {
      if (
        this.customPageActions[activatedPageAction] &&
        this.customPageActions[activatedPageAction]['permissions']
      ) {
        const permissions = [];
        this.customPageActions[activatedPageAction]['permissions'].forEach(permissionName => {
          permissions.push(
            {
              name: AuthorizationHelper.getPermissionName(
                permissionName,
                (this.permissionEntityNameToUse
                  ? this.permissionEntityNameToUse
                  : this.pluralEntityName
                ).replace('-', '_'),
              ),
            },
            {
              name: AuthorizationHelper.getPermissionName(
                permissionName,
                (this.permissionEntityNameToUse
                  ? this.permissionEntityNameToUse
                  : this.pluralEntityName
                ).replace('-', '_'),
                true,
              ),
            },
          );
        });
        this.customPageActions[activatedPageAction][
          'activated'
        ] = this.$authorizationService.hasPermissions(permissions);
      }
    }
  }

  get pageActions() {
    // Invert the order
    return {
      ...this.customPageActions,
      ...this.defaultPageActions,
    };
  }

  public customRowActions = {
    sendMessageUsingTemplate: {
      activated: true,
      type: 'action',
      icon: 'envelope',
      show: true,
      event: 'send-message-using-template',
      text: 'send_message_using_template',
      permissions: ['show'],
    },
  };

  customOnRowAction($event: { event: string; item: any }) {
    switch ($event.event) {
      case 'send-message-using-template':
        this.onSendMessageUsingTemplate($event.item.item);
        break;
    }
  }

  extendUpdateRowActions(entity: any) {
    entity.actions.sendMessageUsingTemplate.show =
      this.$refs.provider &&
      this.$refs.provider.queryConfig.params.status !== 'trash' &&
      !this.$refs.provider.queryConfig.params.onlyTrashed &&
      this.$modulesService.hasModuleActivated(ModulesHelper.COMMUNICATIONS_MODULE_NAME);
  }

  onModelLoaded($event) {
    this.$emit('model-loaded');
  }

  /* Lifecycle hooks */

  // created() {}
}
